import { supabase } from '@/integrations/supabase/client';

export interface UserActivity {
  id: string;
  session_id: string;
  user_id?: string;
  activity_type: string;
  page_url: string;
  element_id?: string;
  element_type?: string;
  metadata: any;
  timestamp: string;
  duration_seconds?: number;
  device_info: any;
}

export interface UserSession {
  id: string;
  session_id: string;
  user_id?: string;
  start_time: string;
  end_time?: string;
  duration_seconds?: number;
  page_count: number;
  device_info: any;
  last_activity: string;
}

export interface ActivityAnalytics {
  totalActivities: number;
  uniqueSessions: number;
  averageSessionDuration: number;
  mostActivePages: Array<{ page_url: string; count: number }>;
  activityTypes: Array<{ activity_type: string; count: number }>;
  hourlyActivity: Array<{ hour: number; count: number }>;
  deviceTypes: Array<{ device: string; count: number }>;
  userJourney: Array<{ page_url: string; sequence: number; avg_time: number }>;
}

export class UserActivityService {
  
  /**
   * Track a user activity
   */
  static async trackActivity(
    sessionId: string,
    activityType: string,
    pageUrl: string,
    options: {
      userId?: string;
      elementId?: string;
      elementType?: string;
      metadata?: Record<string, any>;
      durationSeconds?: number;
      deviceInfo?: Record<string, any>;
    } = {}
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_activities')
        .insert({
          session_id: sessionId,
          user_id: options.userId || null,
          activity_type: activityType,
          page_url: pageUrl,
          element_id: options.elementId || null,
          element_type: options.elementType || null,
          metadata: options.metadata || {},
          duration_seconds: options.durationSeconds || null,
          device_info: options.deviceInfo || {}
        });

      if (error) {
        console.error('Error tracking activity:', error);
      }
    } catch (error) {
      console.error('Error in trackActivity:', error);
    }
  }

  /**
   * Start or update a user session
   */
  static async startSession(
    sessionId: string,
    options: {
      userId?: string;
      deviceInfo?: Record<string, any>;
    } = {}
  ): Promise<void> {
    try {
      // Use upsert to handle the case where session might already exist
      const { error } = await supabase
        .from('user_sessions')
        .upsert({
          session_id: sessionId,
          user_id: options.userId || null,
          device_info: options.deviceInfo || {},
          page_count: 1,
          last_activity: new Date().toISOString()
        }, {
          onConflict: 'session_id'
        });

      if (error) {
        // If upsert fails, try to update the existing session
        const { error: updateError } = await supabase
          .from('user_sessions')
          .update({
            last_activity: new Date().toISOString(),
            user_id: options.userId || null
          })
          .eq('session_id', sessionId);

        if (updateError) {
          console.error('Error updating session after upsert failed:', updateError);
        }
      }
    } catch (error) {
      console.error('Error in startSession:', error);
    }
  }

  /**
   * End a user session
   */
  static async endSession(sessionId: string): Promise<void> {
    try {
      const { data: session } = await supabase
        .from('user_sessions')
        .select('start_time')
        .eq('session_id', sessionId)
        .single();

      if (session) {
        const endTime = new Date();
        const startTime = new Date(session.start_time);
        const durationSeconds = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

        const { error } = await supabase
          .from('user_sessions')
          .update({
            end_time: endTime.toISOString(),
            duration_seconds: durationSeconds
          })
          .eq('session_id', sessionId);

        if (error) {
          console.error('Error ending session:', error);
        }
      }
    } catch (error) {
      console.error('Error in endSession:', error);
    }
  }

  /**
   * Get user activity analytics
   */
  static async getActivityAnalytics(timeRange: number = 30): Promise<ActivityAnalytics> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - timeRange);

      // Get total activities and unique sessions
      const [activitiesRes, sessionsRes] = await Promise.all([
        supabase
          .from('user_activities')
          .select('*', { count: 'exact' })
          .gte('timestamp', startDate.toISOString()),
        
        supabase
          .from('user_sessions')
          .select('session_id, duration_seconds', { count: 'exact' })
          .gte('start_time', startDate.toISOString())
      ]);

      const totalActivities = activitiesRes.count || 0;
      const uniqueSessions = sessionsRes.count || 0;
      
      // Calculate average session duration
      const sessions = sessionsRes.data || [];
      const validDurations = sessions
        .filter(s => s.duration_seconds !== null)
        .map(s => s.duration_seconds);
      const averageSessionDuration = validDurations.length > 0 
        ? validDurations.reduce((a, b) => a + b, 0) / validDurations.length 
        : 0;

      // Get most active pages
      const { data: pageData } = await supabase
        .from('user_activities')
        .select('page_url')
        .gte('timestamp', startDate.toISOString());

      const pageCounts = (pageData || []).reduce((acc, activity) => {
        acc[activity.page_url] = (acc[activity.page_url] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const mostActivePages = Object.entries(pageCounts)
        .map(([page_url, count]) => ({ page_url, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Get activity types
      const { data: activityData } = await supabase
        .from('user_activities')
        .select('activity_type')
        .gte('timestamp', startDate.toISOString());

      const activityCounts = (activityData || []).reduce((acc, activity) => {
        acc[activity.activity_type] = (acc[activity.activity_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const activityTypes = Object.entries(activityCounts)
        .map(([activity_type, count]) => ({ activity_type, count }))
        .sort((a, b) => b.count - a.count);

      // Get hourly activity distribution
      const { data: hourlyData } = await supabase
        .from('user_activities')
        .select('timestamp')
        .gte('timestamp', startDate.toISOString());

      const hourlyCounts = (hourlyData || []).reduce((acc, activity) => {
        const hour = new Date(activity.timestamp).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);

      const hourlyActivity = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        count: hourlyCounts[hour] || 0
      }));

      // Get device types
      const { data: deviceData } = await supabase
        .from('user_sessions')
        .select('device_info')
        .gte('start_time', startDate.toISOString());

      const deviceCounts = (deviceData || []).reduce((acc, session) => {
        const deviceInfo = typeof session.device_info === 'object' ? session.device_info : {};
        const platform = (deviceInfo as any)?.platform || 'Unknown';
        acc[platform] = (acc[platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const deviceTypes = Object.entries(deviceCounts)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count);

      // Simple user journey (most common page sequences)
      const userJourney = mostActivePages.slice(0, 5).map((page, index) => ({
        page_url: page.page_url,
        sequence: index + 1,
        avg_time: averageSessionDuration / mostActivePages.length
      }));

      return {
        totalActivities,
        uniqueSessions,
        averageSessionDuration,
        mostActivePages,
        activityTypes,
        hourlyActivity,
        deviceTypes,
        userJourney
      };
    } catch (error) {
      console.error('Error getting activity analytics:', error);
      return {
        totalActivities: 0,
        uniqueSessions: 0,
        averageSessionDuration: 0,
        mostActivePages: [],
        activityTypes: [],
        hourlyActivity: [],
        deviceTypes: [],
        userJourney: []
      };
    }
  }

  /**
   * Get real-time activity feed
   */
  static async getRecentActivities(limit: number = 50): Promise<UserActivity[]> {
    try {
      const { data, error } = await supabase
        .from('user_activities')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent activities:', error);
        return [];
      }

      return (data || []) as UserActivity[];
    } catch (error) {
      console.error('Error in getRecentActivities:', error);
      return [];
    }
  }

  /**
   * Get session details
   */
  static async getSessionDetails(sessionId: string): Promise<UserSession | null> {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching session details:', error);
        return null;
      }

      return data as UserSession;
    } catch (error) {
      console.error('Error in getSessionDetails:', error);
      return null;
    }
  }
}